/* eslint-disable functional/no-let
  --------
  Used to be able to generate and assign a secret key, every time its useful life expires.
*/
import isNull from "lodash/isNull";
import { random, decrypt as sjclDecrypt, encrypt as sjclEncrypt } from "sjcl";
import type { StoreApi, UseBoundStore } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { StateStorage } from "zustand/middleware";
import { useShallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import type { IStorageInstance, TStorageType } from "..";
import { localStorage, sessionStorage } from "..";

const msInSecond = 1000;
const secondsInMinute = 60;
const sessionAgeInMinutes = 40;
const secretsLifespan = secondsInMinute * sessionAgeInMinutes;
const secretsLifespanInMiliseconds = secretsLifespan * msInSecond;

const wordsNumber = 8;

interface IStore<T> {
  setValue: (value: T | ((prev: T) => T)) => void;
  value: T;
}

// Secret
let secretKey = random.randomWords(wordsNumber);

const generateSecrets = (): void => {
  secretKey = random.randomWords(wordsNumber);
};

// Rotation
setInterval(generateSecrets, secretsLifespanInMiliseconds);

const decrypt = (ciphertext: string): string => {
  return sjclDecrypt(
    secretKey,
    JSON.parse(ciphertext) as sjcl.SjclCipherEncrypted,
  );
};

const encrypt = (plaintext: string): string => {
  return JSON.stringify(sjclEncrypt(secretKey, plaintext));
};

const createStore = <T>(
  key: string,
  defaultValue: T,
  storageProvider: IStorageInstance,
  encrypted = false,
): UseBoundStore<StoreApi<IStore<T>>> => {
  const loadInitialState = (): T => {
    const storedState = storageProvider.getItem(key) as string;

    if (isNull(storedState)) {
      return defaultValue;
    } else if (encrypted) {
      try {
        return JSON.parse(decrypt(storedState)) as T;
      } catch {
        return defaultValue;
      }
    }

    try {
      return JSON.parse(storedState) as T;
    } catch {
      return defaultValue;
    }
  };

  const store = createWithEqualityFn<IStore<T>>()(
    persist(
      (set, get): IStore<T> => ({
        setValue: (newValue: T | ((prev: T) => T)): void => {
          const nextValue =
            typeof newValue === "function"
              ? (newValue as (prev: T) => T)(get().value)
              : newValue;
          set({ value: nextValue });

          void storageProvider.setItem(
            key,
            encrypted
              ? encrypt(JSON.stringify(nextValue))
              : JSON.stringify(nextValue),
          );
        },
        value: loadInitialState(),
      }),
      {
        name: key,
        storage: createJSONStorage(
          (): StateStorage => storageProvider as StateStorage,
        ),
      },
    ),
  );

  return store;
};

// Create a store cache in runtime memory
const stores = new Map<string, UseBoundStore<StoreApi<IStore<unknown>>>>();

const generateStoreKey = (
  key: string,
  type: TStorageType,
  encrypted: boolean,
): string => {
  return `${type}_${encrypted ? "encrypted_" : ""}${key}`;
};

const useStoredState = <T>(
  key: string,
  defaultValue: T,
  type: TStorageType = "session",
  encrypted = false,
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const storageProvider = type === "session" ? sessionStorage : localStorage;
  const storeKey = generateStoreKey(key, type, encrypted);

  // Get or create store
  let store = stores.get(storeKey);
  if (!store) {
    store = createStore(
      key,
      defaultValue,
      storageProvider,
      encrypted,
    ) as UseBoundStore<StoreApi<IStore<unknown>>>;
    stores.set(storeKey, store);
  }

  // Use shallow comparison to prevent unnecessary re-renders
  const { value, setValue } = store(
    useShallow((state): IStore<T> => state as IStore<T>),
  );

  const setAndStore: React.Dispatch<React.SetStateAction<T>> = (
    newValue: React.SetStateAction<T>,
  ): void => {
    const nextValue = newValue instanceof Function ? newValue(value) : newValue;
    setValue(nextValue);
  };

  return [value, setAndStore];
};

// Function to clear stores cache for testing purposes
const clearStoresCache = (): void => {
  stores.clear();
};

export { useStoredState, clearStoresCache };
